import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import { EMPTY, of } from 'rxjs';
import { switchMap, take, takeWhile, map, filter } from 'rxjs/operators';

import { noop } from 'ui-components/default-props/noop';

import { RATES_UPDATED } from '../../services/pusher-service/pusher-contract-messages';
import { createPusherMessageCache } from '../pusher/message-cache';

const completeCheck = (pusherMsg, jobGuids) => {
  return jobGuids.some((jobGuid) => pusherMsg.messageGuid === jobGuid);
};

const timeoutRetryCheck = ({ messages: pusherMessages, retryCount }) => {
  const hasNoMessages = !pusherMessages?.length;
  return !!(hasNoMessages && retryCount < 1);
};

const timeoutCompleteCheck = ({ messages: pusherMessages }) => {
  return pusherMessages?.length >= 1;
};

const timeoutFailureCheck = ({ messages: pusherMessages, retryCount }) => {
  const hasNoMessages = !pusherMessages?.length;
  return !!(hasNoMessages && retryCount >= 1);
};

export const startBulkRatingGetMessages = ({ apiCallback, pusherTimeoutMs = 30000, onError, logContext }) => {
  const jobGuidManager = jobGuidManagerFactory();
  const fallbackOptions = {
    timeoutMs: pusherTimeoutMs,
    jobGuidManager,
    resetTimeoutCheck: noop,
    completeCheck: (pusherMsg, jobGuids) => completeCheck(pusherMsg, jobGuids),
    timeoutCompleteCheck,
    timeoutRetryCheck,
    timeoutFailureCheck,
    onTimeout: onError,
    logContext,
  };
  const { findMessage } = createPusherMessageCache([RATES_UPDATED], fallbackOptions);

  return apiCallback().pipe(
    switchMap(({ rateJobGuids }) => {
      if (!rateJobGuids?.length) {
        return of({ rateJobGuids: [], findMessage$: EMPTY });
      }

      rateJobGuids.forEach((jobGuid) => jobGuidManager.addJobGuid(jobGuid));

      const messageChecker = (pusherMessage) => {
        return rateJobGuids.some((jobGuid) => pusherMessage.messageGuid === jobGuid);
      };

      return of({
        rateJobGuids,
        findMessage$: findMessage(messageChecker).pipe(
          map((pusherMessage) => {
            if (pusherMessage.data?.errorMessage) {
              onError({
                jobGuids: rateJobGuids,
                errorOnMessage: pusherMessage.data.errorMessage,
              });
              return null;
            }
            return { pusherData: pusherMessage.data, messageGuid: pusherMessage.messageGuid };
          }),
          filter((next) => !!next?.pusherData),
          takeWhile((next) => !next?.pusherData?.final, true),
          take(1),
        ),
      });
    }),
  );
};
